import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import MicIcon from '@mui/icons-material/Mic';
import CloudIcon from '@mui/icons-material/Cloud';
import RouterIcon from '@mui/icons-material/Router';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RuleIcon from '@mui/icons-material/Rule';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScienceIcon from '@mui/icons-material/Science';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SavingsIcon from '@mui/icons-material/Savings';
import StarsIcon from '@mui/icons-material/Stars';
import type {
  Preset,
  ProjectSchema,
  QuestionnaireSection,
} from '../../types';
import { FieldRenderer } from './FieldRenderer';
import { isVisible } from '../../utils/expr';

interface Props {
  schema: ProjectSchema;
  onSubmit: (answers: Record<string, unknown>) => void;
  onSkip: () => void;
}

const ICONS: Record<string, React.ReactNode> = {
  people: <PeopleIcon />,
  chat: <ChatIcon />,
  mic: <MicIcon />,
  cloud: <CloudIcon />,
  router: <RouterIcon />,
  timeline: <TimelineIcon />,
  groups: <GroupsIcon />,
  notifications: <NotificationsIcon />,
  rule: <RuleIcon />,
  settings: <SettingsIcon />,
  // Preset-flavoured icons
  science: <ScienceIcon />,
  support_agent: <SupportAgentIcon />,
  voice_over: <RecordVoiceOverIcon />,
  savings: <SavingsIcon />,
  stars: <StarsIcon />,
};

function iconFor(name?: string): React.ReactNode {
  return (name && ICONS[name]) ?? <SettingsIcon />;
}

function PresetCard({
  preset,
  onClick,
}: {
  preset: Preset;
  onClick: () => void;
}) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
        position: 'relative',
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 3,
          transform: 'translateY(-2px)',
        },
        '&:focus-visible': {
          outline: 'none',
          borderColor: 'primary.main',
          boxShadow: 3,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: 'action.hover',
            '& .MuiSvgIcon-root': { fontSize: 22 },
          }}
        >
          {iconFor(preset.icon)}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {preset.name}
          </Typography>
          {/* Reserve vertical space for up to 2 description lines so
              every preset card keeps chips + CTA aligned regardless of
              how long the description is. Longer descriptions clamp. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mt: 0.25,
              minHeight: '2.4em',
              lineHeight: 1.2,
            }}
          >
            {preset.description ?? ''}
          </Typography>
        </Box>
      </Box>

      {/* Highlight chips. Reserve min-height for ~2 chip rows so
          cards with fewer chips keep the CTA aligned. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          mt: 0.5,
          minHeight: 52,
          alignContent: 'flex-start',
        }}
      >
        {(preset.highlights ?? []).map((h, i) => (
          <Chip
            key={i}
            label={h}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.72rem',
              bgcolor: 'primary.50',
              color: 'primary.main',
              fontWeight: 500,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 'auto',
          pt: 0.5,
          color: 'primary.main',
          fontSize: '0.78rem',
          fontWeight: 600,
        }}
      >
        Use this preset
        <ArrowForwardIcon sx={{ fontSize: 14 }} />
      </Box>
    </Paper>
  );
}

export function QuestionnaireRenderer({ schema, onSubmit }: Props) {
  const [answers, setAnswers] = useState<Record<string, unknown>>({
    ...schema.questionnaire.defaults,
  });
  const [activeStep, setActiveStep] = useState(0);
  const q = schema.questionnaire;

  const update = (name: string, value: unknown) =>
    setAnswers((prev) => ({ ...prev, [name]: value }));

  // Visible sections change as answers change (e.g., text/voice gate on use_case).
  const visibleSections: QuestionnaireSection[] = useMemo(
    () => q.sections.filter((s) => isVisible(s.visible_when, answers)),
    [q.sections, answers],
  );

  const clampedStep = Math.min(activeStep, visibleSections.length - 1);

  const handleNext = () => {
    if (clampedStep < visibleSections.length - 1) {
      setActiveStep(clampedStep + 1);
    } else {
      onSubmit(answers);
    }
  };

  const handleStepClick = (idx: number) => setActiveStep(idx);
  const isLastStep = clampedStep === visibleSections.length - 1;

  const handlePresetClick = (preset: Preset) => {
    const next = { ...answers, ...preset.answers };
    setAnswers(next);
    onSubmit(next);
  };

  const presets = q.presets ?? [];

  return (
    <Paper sx={{ p: 3, maxWidth: 920, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom fontWeight={600} textAlign="center">
        Estimate your AWS costs
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
        {schema.project.name}
      </Typography>

      {/* Preset cards — feature-based one-click starts. */}
      {presets.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mb: 1, letterSpacing: 0.8 }}
          >
            Pick a scenario to start
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: `repeat(${Math.min(presets.length, 3)}, 1fr)`,
              },
              gap: 1.5,
            }}
          >
            {presets.map((preset) => (
              <PresetCard
                key={preset.id}
                preset={preset}
                onClick={() => handlePresetClick(preset)}
              />
            ))}
          </Box>
        </Box>
      )}

      <Alert severity="info" sx={{ mb: 2, py: 0.5 }} icon={false}>
        <Typography variant="caption">
          {presets.length > 0
            ? 'Or answer the questions below to customise every value.'
            : 'Answer the questions below to get your estimate.'}
        </Typography>
      </Alert>

      <Divider sx={{ mb: 2 }} />

      <Stepper activeStep={clampedStep} orientation="vertical">
        {visibleSections.map((section, idx) => (
          <Step key={section.title} completed={idx < clampedStep}>
            <StepLabel
              StepIconComponent={() => iconFor(section.icon)}
              onClick={() => handleStepClick(idx)}
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                borderRadius: 1,
                py: 0.5,
                px: 1,
                mx: -1,
              }}
            >
              {section.title}
            </StepLabel>
            <StepContent>
              {section.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {section.description}
                </Typography>
              )}
              {section.fields.map((f) => (
                <FieldRenderer
                  key={f.name}
                  spec={f}
                  value={answers[f.name]}
                  onChange={(v) => update(f.name, v)}
                  params={answers}
                  models={schema.models ?? null}
                />
              ))}
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                >
                  {isLastStep ? 'Continue' : 'Next'}
                </Button>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Paper>
  );
}
